'use client';

/**
 * LinkedInSenderRoutingSection mirrors the email node's MailboxRoutingSection:
 * the operator selects a sender pool first, then optionally describes how AI
 * should route first touches across that pool.
 */

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import {
  FC, useCallback, useEffect, useRef, useState,
} from 'react';

export interface LinkedInSenderSelectionConfig {
  mode: 'ai_prompt';
  prompt: string;
}

// No email address by design: the sender pool is visible to every SDR in the
// workspace and these are personal logins. Keeping the field off the type stops
// a colleague's address being reintroduced into this UI by accident.
export interface LinkedInSenderOption {
  id: number;
  name: string;
}

interface LinkedInSenderRoutingSectionProps {
  config: LinkedInSenderSelectionConfig | null | undefined;
  availableSenders: LinkedInSenderOption[];
  onChange: (config: LinkedInSenderSelectionConfig | null) => void;
}

const AI_PROMPT_MAX_LENGTH = 2000;
const AI_PROMPT_IDLE_DEBOUNCE_MS = 600;

const AI_PROMPT_PLACEHOLDER = `Example: Route healthcare prospects to Maya. Route North America enterprise accounts to Jordan. Default to balanced distribution if ownership is unclear.

The AI sees each recipient's company, role, location, CRM owner, deal context, and custom columns — describe the sender policy in your own words.`;

const LinkedInSenderRoutingSection: FC<LinkedInSenderRoutingSectionProps> = ({
  config,
  availableSenders,
  onChange,
}) => {
  const [localPrompt, setLocalPrompt] = useState(config?.prompt ?? '');
  const [isOpen, setIsOpen] = useState(Boolean((config?.prompt ?? '').trim()));
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedRef = useRef(config?.prompt ?? '');

  useEffect(() => {
    const incoming = config?.prompt ?? '';
    if (incoming !== lastCommittedRef.current) {
      lastCommittedRef.current = incoming;
      setLocalPrompt(incoming);
    }
    if (incoming.trim()) setIsOpen(true);
  }, [config?.prompt]);

  useEffect(() => () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  }, []);

  const commitPrompt = useCallback((value: string) => {
    const trimmed = value.trim();
    if (value === lastCommittedRef.current) return;
    lastCommittedRef.current = value;
    onChange(trimmed ? { mode: 'ai_prompt', prompt: value } : null);
  }, [onChange]);

  const handlePromptChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value.slice(0, AI_PROMPT_MAX_LENGTH);
    setLocalPrompt(value);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => commitPrompt(value), AI_PROMPT_IDLE_DEBOUNCE_MS);
  }, [commitPrompt]);

  const handlePromptBlur = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    commitPrompt(localPrompt);
  }, [commitPrompt, localPrompt]);

  const handleClear = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    setLocalPrompt('');
    lastCommittedRef.current = '';
    onChange(null);
  }, [onChange]);

  const isActive = localPrompt.trim().length > 0;
  const remaining = AI_PROMPT_MAX_LENGTH - localPrompt.length;

  return (
    <div className='mt-1'>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type='button'
            className='flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50'
          >
            <div className='flex items-center gap-2'>
              {isOpen ? (
                <ChevronDown size={16} className='text-slate-500' />
              ) : (
                <ChevronRight size={16} className='text-slate-500' />
              )}
              <Sparkles size={14} className='text-blue-500' />
              <span className='text-sm font-medium text-gray-700'>Sender routing</span>
              {isActive && (
                <span className='ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700'>
                  AI prompt active
                </span>
              )}
            </div>
            <span className='text-xs text-slate-400'>{isActive ? 'On' : 'Off'}</span>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className='mt-2 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3'>
            <div>
              <Label className='text-xs font-medium text-slate-600'>
                Routing rule (natural language)
              </Label>
              <textarea
                className='mt-1 w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                rows={6}
                placeholder={AI_PROMPT_PLACEHOLDER}
                value={localPrompt}
                onChange={handlePromptChange}
                onBlur={handlePromptBlur}
                maxLength={AI_PROMPT_MAX_LENGTH}
              />
              <div className='mt-1 flex justify-between gap-3 text-xs text-slate-400'>
                <span>
                  Leave empty for deterministic balanced distribution across the selected senders.
                </span>
                <span className='shrink-0'>{remaining} chars left</span>
              </div>
            </div>

            <div>
              <Label className='text-xs font-medium text-slate-600'>
                AI picks from these LinkedIn senders
              </Label>
              {availableSenders.length === 0 ? (
                <p className='mt-1 text-xs text-amber-600'>
                  No senders selected yet — choose at least two above for prompt-based routing.
                </p>
              ) : (
                <>
                  <ul className='mt-1 space-y-0.5'>
                    {availableSenders.map((sender) => (
                      <li key={sender.id} className='truncate text-xs text-slate-600'>
                        • {sender.name}
                      </li>
                    ))}
                  </ul>
                  {availableSenders.length === 1 && (
                    <p className='mt-1 text-xs text-amber-600'>
                      Add another sender to enable prompt-based selection.
                    </p>
                  )}
                </>
              )}
            </div>

            <p className='text-xs text-slate-500'>
              <strong>How it works:</strong> On the first LinkedIn touch, AI uses the
              recipient context to choose one sender from this pool. MarketBetter reuses
              that sender on later touches while it remains connected and eligible for
              the campaign. If AI is unavailable, MarketBetter uses deterministic balanced
              distribution.
            </p>

            {isActive && (
              <div className='flex justify-end'>
                <button
                  type='button'
                  onClick={handleClear}
                  className='text-xs text-slate-500 hover:text-red-500'
                >
                  Clear routing rule
                </button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default LinkedInSenderRoutingSection;
